<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import ScamVideo from '../components/Scam/ScamVideo.vue';
import WhyScamPage from '../components/Scam/WhyScamPage.vue';
import HowToProtectYourself from '../components/Scam/HowToProtectYourself.vue';
import OfficialSocialMediaAccounts from '../components/Scam/OfficialSocialMediaAccounts.vue';
import ReportFakeContent from '../components/Scam/ReportFakeContent.vue';

const sections = [ScamVideo, WhyScamPage, HowToProtectYourself, OfficialSocialMediaAccounts, ReportFakeContent];

const sectionEls = ref([]);
const activeIndex = ref(0);
let observer;

onMounted(() => {
    const visibility = Array(sections.length).fill(0);
    const lastGlobalIdx = sections.length - 1;

    observer = new IntersectionObserver(
        (entries) => {
            // 1) Update visibility values
            entries.forEach((entry) => {
                const idx = Number(entry.target.dataset.idx);
                visibility[idx] = entry.intersectionRatio;
            });

            // 2) If the very last element is at least 80% visible, select it directly
            if (visibility[lastGlobalIdx] >= 0.8) {
                activeIndex.value = lastGlobalIdx;

                return;
            }

            // 3) Otherwise, count visible sections
            const visibleIndices = visibility.map((ratio, i) => (ratio > 0 ? i : -1)).filter((i) => i >= 0);

            const count = visibleIndices.length;

            if (count >= 3) {
                // 3 or more visible → select the middle one
                const mid = Math.floor(count / 2);
                activeIndex.value = visibleIndices[mid];
            } else if (count === 2) {
                // exactly 2 visible → always select the first one
                activeIndex.value = visibleIndices[0];
            } else if (count === 1) {
                // only 1 visible → select that one
                activeIndex.value = visibleIndices[0];
            } else {
                // none visible → reset to 0
                activeIndex.value = 0;
            }
        },
        {
            threshold: Array.from({ length: 101 }, (_, i) => i / 100)
        }
    );

    sectionEls.value.forEach((el, idx) => {
        if (el) {
            el.dataset.idx = idx;
            observer.observe(el);
        }
    });
});
onBeforeUnmount(() => {
    observer.disconnect();
});
</script>
<template>
    <div>
        <WarningsBackground />

        <!-- Titles -->
        <div class="flex flex-col items-center justify-center my-12">
            <h2 class="text-2xl font-bold mb-2">{{ $t('scampage.subheading') }}</h2>
            <h1 class="text-4xl font-semibold text-red">{{ $t('scampage.heading') }}</h1>
        </div>

        <!-- Content Sections: only first or middle visible item scales -->
        <div
            v-for="(Component, index) in sections"
            :key="index"
            :ref="(element) => (sectionEls[index] = element)"
            class="transform transition-transform duration-200"
            :class="activeIndex === index ? 'scale-105' : 'scale-95'"
        >
            <component :is="Component" />
        </div>

        <!-- Legal Footer -->
        <section class="text-center py-10 px-4">
            <div class="max-w-5xl mx-auto space-y-2">
                <p class="text-sm text-gray-600">
                    {{ $t('scampage.legalFooter') }}
                </p>
                <p class="text-sm font-semibold uppercase">
                    {{ $t('scamPage.legalNotice') }}
                </p>
            </div>
        </section>
    </div>
</template>

<style>
.section-white {
    @apply bg-white rounded-lg shadow p-16 my-16;
}
</style>
